/**
 * Icons Utility - PSP/CRE Tool v3.0
 * 
 * Centralized icon definitions to ensure consistency and avoid encoding issues.
 * All icons use Unicode characters that are safe across all platforms.
 * 
 * UPDATED: December 3, 2024 - Added spatial configuration icons
 * UPDATED: December 23, 2024 - Added FLAG_ICONS for national standards
 * UPDATED: January 13, 2025 - v2.1: Added SUPPLY_ICONS for logistics display
 *                            Added MEDICAL_ICONS.BLOOD for blood products
 * UPDATED: January 19, 2025 - v2.2: Fixed MED-001 to MED-008 missing icons
 * UPDATED: March 15, 2026 - v2.3: Added FLAG_ICONS.FRENCH alias for nationalLogistics.js
 *   standard-code naming convention (cf. ROMANIAN, GERMAN, BRITISH, POLISH).
 *   FLAG_ICONS.FRANCE already existed; FRENCH is the alias consumed by
 *   nationalLogistics.js AVAILABLE_NATIONAL_STANDARDS and NATIONAL_LOGISTICS_OVERRIDES.
 *                            Added STATUS_ICONS.BLUE, POWER_ON, POWER_LOW
 *                            Added GENERAL_ICONS.RADIO
 *                            Added WEATHER_ICONS.PARTLY_CLOUDY
 *                            Added MEDICAL_ICONS.FIRST_AID
 * 
 * Usage:
 * import { ICONS, FLAG_ICONS, SUPPLY_ICONS, TRANSPORT_ICONS } from '../utils/icons';
 * console.log(ICONS.MEDICAL.AMBULANCE); // 🚑
 * console.log(FLAG_ICONS.ROMANIAN); // 🇷🇴
 * console.log(SUPPLY_ICONS.IV_FLUIDS); // 💧
 * console.log(TRANSPORT_ICONS.HELICOPTER); // 🚁
 * 
 * @author Dr. Tatar Radu MD, Romanian Military Medical Institute
 * @date January 19, 2025
 * @version 2.3
 */

// ============================================================================
// MEDICAL ICONS
// ============================================================================

export const MEDICAL_ICONS = {
  // Medical Vehicles
  AMBULANCE: '🚑',
  HELICOPTER: '🚁',
  MEDEVAC: '🚁',
  
  // Medical Facilities
  HOSPITAL: '🏥',
  TENT: '⛺',
  RED_CROSS: '✚',
  MEDICAL_SYMBOL: '⚕️',
  FIRST_AID: '🩹',  // v2.2 FIX: Added for basic medical facilities (MED-001)
  
  // Medical Equipment
  SYRINGE: '💉',
  PILL: '💊',
  BANDAGE: '🩹',
  STETHOSCOPE: '🩺',
  
  // Personnel
  DOCTOR: '👨‍⚕️',
  NURSE: '👩‍⚕️',
  MEDIC: '⚕️',
  STRETCHER: '🛌',
  
  // Status
  HEARTBEAT: '💓',
  HEART: '❤️',
  SKULL: '💀',
  INJURED: '🤕',
  
  // Blood (v2.1 - January 13, 2025)
  BLOOD: '🩸',
  BLOOD_DROP: '🩸'
};

// ============================================================================
// SUPPLY ICONS (v2.1 - January 13, 2025)
// For Tab3/Tab4 logistics and supply chain display
// ============================================================================

export const SUPPLY_ICONS = {
  // Medical Supplies
  IV_FLUIDS: '💧',
  DRESSINGS: '🩹',
  MEDICATIONS: '💊',
  BLOOD_PRODUCTS: '🩸',
  
  // General Supplies
  FOOD_RATIONS: '🍱',
  FOOD: '🍱',
  RATIONS: '🍱',
  WATER: '💧',
  
  // Packaging
  PACKAGE: '📦',
  BOX: '📦',
  CRATE: '📦',
  
  // Indicators
  SUPPLY_LOW: '⚠️',
  SUPPLY_OK: '✅',
  SUPPLY_CRITICAL: '🚨'
};

// ============================================================================
// MILITARY ICONS
// ============================================================================

export const MILITARY_ICONS = {
  // Units
  BATTALION: '🪖',
  BRIGADE: '🎖️',
  DIVISION: '⭐',
  CORPS: '🎗️',
  
  // Personnel
  SOLDIER: '🪖',
  OFFICER: '👮',
  GENERAL: '⭐',
  
  // Weapons & Combat
  RIFLE: '🔫',
  TANK: '🛡️',
  ARTILLERY: '💥',
  EXPLOSION: '💥',
  BOMB: '💣',
  MISSILE: '🚀',
  
  // Operations
  TARGET: '🎯',
  CROSSHAIR: '⌖',
  BINOCULARS: '🔭',
  RADAR: '📡',
  
  // Positions
  SHIELD: '🛡️',
  SWORD: '⚔️',
  FLAG: '🚩',
  CHECKPOINT: '🚧'
};

// ============================================================================
// FLAG ICONS - National Standards (NEW - December 23, 2024)
// ============================================================================

export const FLAG_ICONS = {
  // Primary Standards (used in PSP/CRE Tool)
  ROMANIAN: '🇷🇴',
  ROMANIA: '🇷🇴',   // Alias for nationalStandards.js compatibility
  NATO: '🔵',        // NATO blue — no NATO flag emoji exists; blue circle represents alliance colour (ICON-001 fix)
  US: '🇺🇸',
  
  // NATO Member Nations
  ALBANIA: '🇦🇱',
  BELGIUM: '🇧🇪',
  BULGARIA: '🇧🇬',
  CANADA: '🇨🇦',
  CROATIA: '🇭🇷',
  CZECH: '🇨🇿',
  DENMARK: '🇩🇰',
  ESTONIA: '🇪🇪',
  FINLAND: '🇫🇮',
  FRANCE: '🇫🇷',
  FRENCH: '🇫🇷',   // Alias for nationalLogistics.js standard-code convention (cf. ROMANIAN, GERMAN, BRITISH, POLISH)
  GERMANY: '🇩🇪',
  GREECE: '🇬🇷',
  HUNGARY: '🇭🇺',
  ICELAND: '🇮🇸',
  ITALY: '🇮🇹',
  LATVIA: '🇱🇻',
  LITHUANIA: '🇱🇹',
  LUXEMBOURG: '🇱🇺',
  MONTENEGRO: '🇲🇪',
  NETHERLANDS: '🇳🇱',
  NORTH_MACEDONIA: '🇲🇰',
  NORWAY: '🇳🇴',
  POLAND: '🇵🇱',
  PORTUGAL: '🇵🇹',
  SLOVAKIA: '🇸🇰',
  SLOVENIA: '🇸🇮',
  SPAIN: '🇪🇸',
  SWEDEN: '🇸🇪',
  TURKEY: '🇹🇷',
  UK: '🇬🇧',
  USA: '🇺🇸',
  
  // Partner Nations (common in multinational operations)
  UKRAINE: '🇺🇦',
  GEORGIA: '🇬🇪',
  MOLDOVA: '🇲🇩',
  AUSTRALIA: '🇦🇺',
  NEW_ZEALAND: '🇳🇿',
  JAPAN: '🇯🇵',
  SOUTH_KOREA: '🇰🇷',
  
  // Special Symbols
  INTERNATIONAL: '🌐',
  MULTINATIONAL: '🌍',
  UN: '🇺🇳',
  EU: '🇪🇺',
  RED_CROSS: '🏥',
  WHITE_FLAG: '🏳️'
};

// ============================================================================
// NAVIGATION & ARROWS
// ============================================================================

export const ARROW_ICONS = {
  // Directional Arrows
  UP: '↑',
  DOWN: '↓',
  LEFT: '←',
  RIGHT: '→',
  
  UP_DOWN: '↕',
  LEFT_RIGHT: '↔',
  
  // Diagonal Arrows
  UP_RIGHT: '↗',
  UP_LEFT: '↖',
  DOWN_RIGHT: '↘',
  DOWN_LEFT: '↙',
  
  // Double Arrows
  DOUBLE_UP: '⇈',
  DOUBLE_DOWN: '⇊',
  DOUBLE_LEFT: '⇇',
  DOUBLE_RIGHT: '⇉',
  
  // Curved Arrows
  CURVED_LEFT: '↶',
  CURVED_RIGHT: '↷',
  RETURN: '↩',
  UNDO: '↶',
  REDO: '↷',
  
  // Special Arrows
  FORWARD: '→',
  BACKWARD: '←',
  NEXT: '▶',
  PREVIOUS: '◀',
  PLAY: '▶️',
  PAUSE: '⏸️',
  STOP: '⏹️',
  
  // Thick Arrows
  THICK_UP: '⬆',
  THICK_DOWN: '⬇',
  THICK_LEFT: '⬅',
  THICK_RIGHT: '➡'
};

// ============================================================================
// STATUS & ALERTS
// ============================================================================

export const STATUS_ICONS = {
  // Traffic Light System
  GREEN: '🟢',
  YELLOW: '🟡',
  RED: '🔴',
  BLUE: '🔵',  // v2.2 FIX: Added for info/low-risk indicators (MED-001 to MED-008)
  
  // Power Status (v2.2 FIX: Added for infrastructureStatus.js)
  POWER_ON: '⚡',
  POWER_LOW: '🔋',
  
  // Alternative Status
  GREEN_CHECK: '✅',
  YELLOW_WARNING: '⚠️',
  RED_X: '❌',
  
  // Symbols
  CHECK: '✓',
  CHECK_MARK: '✔',
  CROSS: '✗',
  X_MARK: '✖',
  
  // Alert Levels
  INFO: 'ℹ️',
  WARNING: '⚠️',
  ERROR: '🛑',
  CRITICAL: '🚨',
  ALERT: '🔔',
  
  // Status Indicators
  OK: '👍',
  NOT_OK: '👎',
  ATTENTION: '⚠️',
  CAUTION: '⚠️',
  DANGER: '☢️',
  STOP: '🛑',
  
  // Progress
  LOADING: '⏳',
  WAITING: '⌛',
  COMPLETE: '✅',
  INCOMPLETE: '⭕',
  IN_PROGRESS: '🔄'
};

// ============================================================================
// TRANSPORTATION (UPDATED for spatial configuration)
// ============================================================================

export const TRANSPORT_ICONS = {
  // Ground Vehicles
  CAR: '🚗',
  TRUCK: '🚚',
  BUS: '🚌',
  AMBULANCE: '🚑',
  
  // Military Vehicles
  TANK: '🛡️',
  ARMORED: '🚛',
  JEEP: '🚙',
  
  // Air Transport
  HELICOPTER: '🚁',
  AIRPLANE: '✈️',
  JET: '🛩️',
  DRONE: '🛸',
  
  // Rail Transport (NEW for spatial configuration)
  TRAIN: '🚂',
  TRAIN_STATION: '🚉',
  RAILWAY: '🚃',
  
  // Symbols
  WHEEL: '⚙️',
  SPEED: '💨',
  ROAD: '🛣️',
  RUNWAY: '🛫'
};

// ============================================================================
// WEATHER & ENVIRONMENT
// ============================================================================

export const WEATHER_ICONS = {
  // Basic Weather
  SUN: '☀️',
  CLOUD: '☁️',
  PARTLY_CLOUDY: '⛅',  // v2.2 FIX: Added for temperate/subtropical climate (MED-006, MED-007)
  RAIN: '🌧️',
  SNOW: '❄️',
  STORM: '⛈️',
  THUNDER: '⚡',
  FOG: '🌫️',
  WIND: '💨',
  
  // Temperature
  HOT: '🔥',
  COLD: '🧊',
  THERMOMETER: '🌡️',
  
  // Terrain
  MOUNTAIN: '⛰️',
  FOREST: '🌲',
  DESERT: '🏜️',
  WATER: '💧',
  RIVER: '🌊',
  
  // Seasons (NEW for rasputitsa modeling)
  MUD: '🟫',  // For rasputitsa (mud season)
  ICE: '🧊',  // For winter frozen
  
  // Time
  DAY: '☀️',
  NIGHT: '🌙',
  SUNRISE: '🌅',
  SUNSET: '🌇'
};

// ============================================================================
// OPERATIONS & TACTICAL
// ============================================================================

export const TACTICAL_ICONS = {
  // Positions
  LEFT: '⬅️',
  CENTER: '🎯',
  RIGHT: '➡️',
  
  // Missions
  ATTACK: '⚔️',
  DEFEND: '🛡️',
  MARCH: '➡️',
  RESERVE: '📦',
  
  // Special Operations
  RIVER_CROSSING: '🌊',
  DELAY: '⏱️',
  WITHDRAWAL: '⬅️',
  EXPLOITATION: '🚀',
  
  // Tactical Symbols
  MAIN_EFFORT: '⚔️',
  SUPPORTING: '🛡️',
  OBJECTIVE: '🎯',
  CHECKPOINT: '📍',
  
  // Intelligence
  RECON: '🔭',
  RADAR: '📡',
  SATELLITE: '🛰️',
  INTEL: '🔍'
};

// ============================================================================
// FACILITIES & INFRASTRUCTURE (UPDATED for spatial configuration)
// ============================================================================

export const FACILITY_ICONS = {
  // Medical Treatment Facilities
  ROLE1: '⛺',      // Battalion Aid Station
  ROLE2: '🏕️',     // Forward Surgical Team
  ROLE3: '🏥',     // Combat Support Hospital
  ROLE4: '🏛️',     // Definitive Care
  
  // Support Facilities
  CSU: '📦',       // Casualty Staging Unit
  PHA: '🏠',       // Patient Holding Area
  HLZ: '🚁',       // Helicopter Landing Zone
  AXP: '🚑',       // Ambulance Exchange Point (NEW)
  
  // Infrastructure (NEW for spatial configuration)
  AIRFIELD: '✈️',  // Tactical Airfield
  TRAIN_STATION: '🚉',  // Railway Medical Evacuation Point
  RAILWAY: '🚃',   // Railway infrastructure
  
  // Buildings
  BUILDING: '🏢',
  TENT: '⛺',
  BUNKER: '🏰',
  BASE: '🏕️',
  WAREHOUSE: '🏭',
  
  // Utilities
  POWER: '⚡',
  WATER: '💧',
  FUEL: '⛽',
  ANTENNA: '📡',

  // MTF Operational Status (IMP-MARCH-001 — for movement_backlog display in Tab5/6)
  MTF_CLOSED:  '🔒',  // MTF packed for movement — not operational
  MTF_SETUP:   '🔧',  // MTF setting up after halt — partial capacity
  MTF_OPEN:    '🔓',  // MTF fully operational
  MTF_BACKLOG: '⏳'   // Patients waiting due to MTF closure during march
};

// ============================================================================
// DATA & STATISTICS
// ============================================================================

export const DATA_ICONS = {
  // Charts & Graphs
  CHART_UP: '📈',
  CHART_DOWN: '📉',
  BAR_CHART: '📊',
  PIE_CHART: '📊',
  
  // Documents
  DOCUMENT: '📄',
  REPORT: '📋',
  CLIPBOARD: '📋',
  FOLDER: '📁',
  FILE: '📎',
  
  // Numbers & Math
  HASH: '#',
  PLUS: '+',
  MINUS: '-',
  MULTIPLY: '×',
  DIVIDE: '÷',
  EQUALS: '=',
  PERCENT: '%',
  
  // Statistics
  TRENDING_UP: '📈',
  TRENDING_DOWN: '📉',
  STABLE: '➡️',
  
  // Data Operations
  REFRESH: '🔄',
  SYNC: '🔃',
  DOWNLOAD: '⬇️',
  UPLOAD: '⬆️',
  SAVE: '💾',
  LOAD: '📂'
};

// ============================================================================
// TIME & CALENDAR
// ============================================================================

export const TIME_ICONS = {
  // Clocks
  CLOCK: '🕐',
  STOPWATCH: '⏱️',
  TIMER: '⏲️',
  HOURGLASS: '⌛',
  HOURGLASS_FLOWING: '⏳',
  
  // Calendar
  CALENDAR: '📅',
  DATE: '📆',
  
  // Time Periods
  DAY: '☀️',
  NIGHT: '🌙',
  MORNING: '🌅',
  EVENING: '🌇',
  
  // Duration
  FAST: '⚡',
  SLOW: '🐌',
  URGENT: '🚨',
  DELAYED: '⏰'
};

// ============================================================================
// UI ELEMENTS
// ============================================================================

export const UI_ICONS = {
  // Actions
  EDIT: '✏️',
  DELETE: '🗑️',
  ADD: '➕',
  REMOVE: '➖',
  SEARCH: '🔍',
  FILTER: '🔽',
  SORT: '⇅',
  
  // Navigation
  HOME: '🏠',
  MENU: '☰',
  SETTINGS: '⚙️',
  HELP: '❓',
  INFO: 'ℹ️',
  MAP: '🗺️',  // NEW for spatial visualization
  
  // User Actions
  SAVE: '💾',
  CANCEL: '❌',
  CONFIRM: '✅',
  RESET: '🔄',
  UNDO: '↶',
  REDO: '↷',
  
  // Expansion
  EXPAND: '▼',
  COLLAPSE: '▲',
  EXPAND_MORE: '⬇️',
  COLLAPSE_MORE: '⬆️',
  
  // Selection
  CHECKBOX_CHECKED: '☑',
  CHECKBOX_UNCHECKED: '☐',
  RADIO_CHECKED: '⦿',
  RADIO_UNCHECKED: '○',
  
  // Visibility
  VISIBLE: '👁️',
  HIDDEN: '🙈',
  LOCK: '🔒',
  UNLOCK: '🔓',
  LOCK_OPEN: '🔓'  // Explicit open lock for clarity
};

// ============================================================================
// GENERAL SYMBOLS
// ============================================================================

export const GENERAL_ICONS = {
  // Basic Shapes
  CIRCLE: '●',
  SQUARE: '■',
  TRIANGLE: '▲',
  DIAMOND: '◆',
  STAR: '★',
  
  // Filled vs Outline
  CIRCLE_FILLED: '●',
  CIRCLE_OUTLINE: '○',
  SQUARE_FILLED: '■',
  SQUARE_OUTLINE: '□',
  
  // Bullets & Lists
  BULLET: '•',
  DOT: '·',
  ARROW_BULLET: '▸',
  
  // Separators
  DASH: '–',
  LONG_DASH: '—',
  VERTICAL_BAR: '|',
  SLASH: '/',
  BACKSLASH: '\\',
  
  // Miscellaneous
  GEAR: '⚙️',
  WRENCH: '🔧',
  HAMMER: '🔨',
  KEY: '🔑',
  LOCK: '🔒',
  UNLOCK: '🔓',
  BELL: '🔔',
  TROPHY: '🏆',
  MEDAL: '🏅',
  BOOKMARK: '🔖',
  RADIO: '📻',  // v2.2 FIX: Added for communications status (MED-003, MED-004)
};

// ============================================================================
// TERRAIN ICONS (for climateZones.js and other terrain-related files)
// ============================================================================

export const TERRAIN_ICONS = {
  // Terrain Types
  MOUNTAIN: '⛰️',
  FOREST: '🌲',
  DESERT: '🏜️',
  URBAN: '🏙️',
  WATER: '💧',
  RIVER: '🌊',
  MARSH: '🌿',
  PLAINS: '🌾',
  HILLS: '⛰️',
  JUNGLE: '🌴',
  ARCTIC: '❄️',
  COASTAL: '🏖️',
  
  // Terrain Conditions
  MUD: '🟫',
  ICE: '🧊',
  SAND: '🏜️',
  ROCK: '🪨',
  
  // Mixed/Complex
  MIXED: '🗺️',
  COMPLEX: '🗺️',
  OPEN: '🌅',
  ROLLING: '🌄'
};

// ============================================================================
// COMBINED EXPORT (For convenience)
// ============================================================================

export const ICONS = {
  MEDICAL: MEDICAL_ICONS,
  MILITARY: MILITARY_ICONS,
  FLAG: FLAG_ICONS,        // Added December 23, 2024
  ARROW: ARROW_ICONS,
  STATUS: STATUS_ICONS,
  TRANSPORT: TRANSPORT_ICONS,
  WEATHER: WEATHER_ICONS,
  TERRAIN: TERRAIN_ICONS,  // Added December 23, 2024 - for climateZones.js
  TACTICAL: TACTICAL_ICONS,
  FACILITY: FACILITY_ICONS,
  DATA: DATA_ICONS,
  TIME: TIME_ICONS,
  UI: UI_ICONS,
  GENERAL: GENERAL_ICONS,
  SUPPLY: SUPPLY_ICONS     // Added January 13, 2025 - for Tab3/Tab4 logistics
};

// ============================================================================
// HELPER FUNCTIONS (UPDATED with new spatial functions)
// ============================================================================

/**
 * Get status icon based on percentage threshold
 * @param {number} percentage - Value from 0-100
 * @param {object} options - Optional configuration
 * @returns {string} Appropriate status icon
 */
export function getStatusIconByPercentage(percentage, options = {}) {
  const {
    greenThreshold = 75,
    yellowThreshold = 90,
    useCircles = true
  } = options;
  
  if (useCircles) {
    if (percentage < greenThreshold) return STATUS_ICONS.GREEN;
    if (percentage < yellowThreshold) return STATUS_ICONS.YELLOW;
    return STATUS_ICONS.RED;
  } else {
    if (percentage < greenThreshold) return STATUS_ICONS.GREEN_CHECK;
    if (percentage < yellowThreshold) return STATUS_ICONS.YELLOW_WARNING;
    return STATUS_ICONS.RED_X;
  }
}

/**
 * Get MTF role icon
 * @param {string} role - MTF role (ROLE1, ROLE2, ROLE3, ROLE4)
 * @returns {string} Appropriate facility icon
 */
export function getMTFIcon(role) {
  const roleMap = {
    'ROLE1': FACILITY_ICONS.ROLE1,
    'ROLE2_BASIC': FACILITY_ICONS.ROLE2,
    'ROLE2_ENHANCED': FACILITY_ICONS.ROLE2,
    'ROLE3': FACILITY_ICONS.ROLE3,
    'ROLE4': FACILITY_ICONS.ROLE4
  };
  return roleMap[role] || FACILITY_ICONS.ROLE1;
}

/**
 * Get infrastructure node icon (NEW for spatial configuration)
 * @param {string} nodeType - HLZ, AIRFIELD, TRAIN_STATION, AXP
 * @returns {string} Appropriate infrastructure icon
 */
export function getInfrastructureIcon(nodeType) {
  const nodeMap = {
    'HLZ': FACILITY_ICONS.HLZ,
    'AIRFIELD': FACILITY_ICONS.AIRFIELD,
    'TRAIN_STATION': FACILITY_ICONS.TRAIN_STATION,
    'AXP': FACILITY_ICONS.AXP
  };
  return nodeMap[nodeType] || FACILITY_ICONS.HLZ;
}

/**
 * Get tactical position icon
 * @param {string} position - LEFT, CENTER, RIGHT
 * @returns {string} Appropriate position icon
 */
export function getPositionIcon(position) {
  const positionMap = {
    'LEFT': TACTICAL_ICONS.LEFT,
    'CENTER': TACTICAL_ICONS.CENTER,
    'RIGHT': TACTICAL_ICONS.RIGHT,
    'FAR_LEFT': ARROW_ICONS.THICK_LEFT,
    'FAR_RIGHT': ARROW_ICONS.THICK_RIGHT
  };
  return positionMap[position] || TACTICAL_ICONS.CENTER;
}

/**
 * Get mission icon
 * @param {string} mission - MAIN_EFFORT, SUPPORTING, RESERVE, etc.
 * @returns {string} Appropriate mission icon
 */
export function getMissionIcon(mission) {
  const missionMap = {
    'MAIN_EFFORT': TACTICAL_ICONS.MAIN_EFFORT,
    'SUPPORTING': TACTICAL_ICONS.SUPPORTING,
    'RESERVE': TACTICAL_ICONS.RESERVE,
    'EXPLOITATION': TACTICAL_ICONS.EXPLOITATION
  };
  return missionMap[mission] || TACTICAL_ICONS.OBJECTIVE;
}

/**
 * Get posture icon
 * @param {string} posture - ATTACK, DEFEND, MARCH, etc.
 * @returns {string} Appropriate posture icon
 */
export function getPostureIcon(posture) {
  const postureMap = {
    'DELIBERATE_ATTACK': TACTICAL_ICONS.ATTACK,
    'HASTY_ATTACK': TACTICAL_ICONS.ATTACK,
    'DELIBERATE_DEFENSE': TACTICAL_ICONS.DEFEND,
    'HASTY_DEFENSE': TACTICAL_ICONS.DEFEND,
    'MARCH': TACTICAL_ICONS.MARCH,
    'RIVER_CROSSING': TACTICAL_ICONS.RIVER_CROSSING,
    'DELAY': TACTICAL_ICONS.DELAY,
    'WITHDRAWAL': TACTICAL_ICONS.WITHDRAWAL,
    'RESERVE': TACTICAL_ICONS.RESERVE
  };
  return postureMap[posture] || TACTICAL_ICONS.OBJECTIVE;
}

/**
 * Get transport icon
 * @param {string} type - AMBULANCE, HELICOPTER, etc.
 * @returns {string} Appropriate transport icon
 */
export function getTransportIcon(type) {
  const typeMap = {
    'AMBULANCE_WHEELED': TRANSPORT_ICONS.AMBULANCE,
    'AMBULANCE_TRACKED': TRANSPORT_ICONS.ARMORED,
    'HELICOPTER_LIGHT': TRANSPORT_ICONS.HELICOPTER,
    'HELICOPTER_MEDIUM': TRANSPORT_ICONS.HELICOPTER,
    'HELICOPTER_HEAVY': TRANSPORT_ICONS.HELICOPTER,
    'TRAIN': TRANSPORT_ICONS.TRAIN
  };
  return typeMap[type] || TRANSPORT_ICONS.AMBULANCE;
}

/**
 * Get season icon (NEW for rasputitsa modeling)
 * @param {string} season - SPRING_RASPUTITSA, WINTER_FROZEN, etc.
 * @returns {string} Appropriate season icon
 */
export function getSeasonIcon(season) {
  const seasonMap = {
    'SPRING_RASPUTITSA': WEATHER_ICONS.MUD,
    'AUTUMN_RASPUTITSA': WEATHER_ICONS.MUD,
    'WINTER_FROZEN': WEATHER_ICONS.ICE,
    'SUMMER_DRY': WEATHER_ICONS.SUN
  };
  return seasonMap[season] || WEATHER_ICONS.SUN;
}

/**
 * Get flag icon for national standard (NEW - December 23, 2024)
 * @param {string} standard - ROMANIAN, NATO, US, etc.
 * @returns {string} Appropriate flag icon
 */
export function getFlagIcon(standard) {
  const standardUpper = (standard || '').toUpperCase();
  return FLAG_ICONS[standardUpper] || FLAG_ICONS.NATO;
}

/**
 * Format number with icon
 * @param {number} value - Number to format
 * @param {string} icon - Icon to use
 * @param {string} separator - Separator between icon and number (default: space)
 * @returns {string} Formatted string
 */
export function formatWithIcon(value, icon, separator = ' ') {
  return `${value}${separator}${icon}`;
}

/**
 * Get multiplication sign (safe character)
 * @returns {string} Multiplication character
 */
export function getMultiplySign() {
  return DATA_ICONS.MULTIPLY; // ×
}

// ============================================================================
// USAGE EXAMPLES (for documentation) - UPDATED
// ============================================================================

/*
EXAMPLE USAGE:

// Import the icons
import { ICONS, FLAG_ICONS, getStatusIconByPercentage, getMTFIcon, getInfrastructureIcon, getFlagIcon } from '../utils/icons';

// Use in React components:
<Typography>
  {ICONS.MEDICAL.AMBULANCE} Ambulance
</Typography>

<Typography>
  {ICONS.STATUS.GREEN} Operational
</Typography>

// NEW: Flag icons for national standards
<Typography>
  {FLAG_ICONS.ROMANIAN} Romanian Standard
</Typography>

<Typography>
  {FLAG_ICONS.NATO} NATO Standard
</Typography>

<Typography>
  {FLAG_ICONS.US} US Standard
</Typography>

// Get flag dynamically
const flag = getFlagIcon('ROMANIAN'); // Returns 🇷🇴

// NEW: Spatial configuration icons
<Typography>
  {ICONS.FACILITY.TRAIN_STATION} Medical Evacuation Train Station
</Typography>

<Typography>
  {ICONS.FACILITY.AIRFIELD} Tactical Airfield
</Typography>

<Typography>
  {ICONS.FACILITY.HLZ} Helicopter Landing Zone
</Typography>

// Use helper functions:
const statusIcon = getStatusIconByPercentage(85); // Returns 🟡
const mtfIcon = getMTFIcon('ROLE2_BASIC'); // Returns 🏕️
const infraIcon = getInfrastructureIcon('TRAIN_STATION'); // Returns 🚉

// National standard selector with flags:
const standards = [
  { id: 'ROMANIAN', label: `${FLAG_ICONS.ROMANIAN} Romanian` },
  { id: 'NATO', label: `${FLAG_ICONS.NATO} NATO` },
  { id: 'US', label: `${FLAG_ICONS.US} US Army` }
];

// Spatial map nodes:
const nodes = {
  hlz: `${ICONS.FACILITY.HLZ} Primary HLZ`,
  airfield: `${ICONS.FACILITY.AIRFIELD} Main Airfield`,
  train: `${ICONS.FACILITY.TRAIN_STATION} Train Station`,
  axp: `${ICONS.FACILITY.AXP} Ambulance Exchange Point`
};

// Rasputitsa warning:
{season === 'SPRING_RASPUTITSA' && (
  <Alert severity="warning">
    {ICONS.WEATHER.MUD} Mud Season Active - Evacuation Degraded
  </Alert>
)}

// Use in lists:
const items = [
  `${ICONS.ARROW.RIGHT} Item 1`,
  `${ICONS.ARROW.RIGHT} Item 2`,
  `${ICONS.ARROW.RIGHT} Item 3`
];

// Format numbers:
const count = formatWithIcon(3, ICONS.DATA.MULTIPLY, ' '); // "3 ×"

// Status displays:
{capacity > 90 ? ICONS.STATUS.RED : ICONS.STATUS.GREEN} Capacity: {capacity}%

// Navigation:
<Button>{ICONS.ARROW.BACKWARD} Previous</Button>
<Button>Next {ICONS.ARROW.FORWARD}</Button>

// Medical display:
{ICONS.MEDICAL.AMBULANCE} {ambulanceCount} Ambulances
{ICONS.TRANSPORT.HELICOPTER} {helicopterCount} Helicopters
{ICONS.TRANSPORT.TRAIN} {trainCount} Medical Trains

// Tactical display:
{ICONS.TACTICAL.LEFT} Left Flank
{ICONS.TACTICAL.CENTER} Center
{ICONS.TACTICAL.RIGHT} Right Flank

// Multinational force display:
{FLAG_ICONS.ROMANIAN} 1st Romanian Brigade
{FLAG_ICONS.US} 2nd US BCT
{FLAG_ICONS.UK} UK Medical Group
*/

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default ICONS;
